import { useState } from "react";

import { FolderCog, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  useCreateSoftwareType,
  useDeleteSoftwareType,
  useSoftwareTypes,
  useUpdateSoftwareType,
} from "@/features/servers/hooks/useSoftwareTypes";
import type { ISoftwareType } from "@/features/servers/schemas/servers";
import { cn } from "@/shared/lib/utils";

interface SoftwareTypesDialogProps {
  open: boolean;
  onClose: () => void;
}

type EditorMode = "idle" | "create" | "edit";

export function SoftwareTypesDialog({ open, onClose }: SoftwareTypesDialogProps) {
  const { data: types = [], isLoading } = useSoftwareTypes();
  const createType = useCreateSoftwareType();
  const updateType = useUpdateSoftwareType();
  const deleteType = useDeleteSoftwareType();

  const [mode, setMode] = useState<EditorMode>("idle");
  const [editing, setEditing] = useState<ISoftwareType | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [key, setKey] = useState("");
  const [order, setOrder] = useState(0);
  const [active, setActive] = useState(true);

  const resetEditor = () => {
    setMode("idle");
    setEditing(null);
    setDisplayName("");
    setKey("");
    setOrder(0);
    setActive(true);
  };

  const startCreate = () => {
    setMode("create");
    setEditing(null);
    setDisplayName("");
    setKey("");
    setOrder(types.length + 1);
    setActive(true);
  };

  const startEdit = (item: ISoftwareType) => {
    setMode("edit");
    setEditing(item);
    setDisplayName(item.displayName);
    setKey(item.key);
    setOrder(item.order);
    setActive(item.active);
  };

  const handleSave = async () => {
    const name = displayName.trim();
    const k = key.trim().toLowerCase().replace(/\s+/g, "_");
    if (!name || !k) {
      toast.error("Nombre y key son obligatorios");
      return;
    }

    try {
      if (mode === "create") {
        await createType.mutateAsync({
          displayName: name,
          key: k,
          order,
          active,
        });
        toast.success(`Tipo "${name}" creado`);
      } else if (mode === "edit" && editing) {
        await updateType.mutateAsync({
          id: editing.id,
          data: { displayName: name, key: k, order, active },
        });
        toast.success(`Tipo "${name}" actualizado`);
      }
      resetEditor();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al guardar");
    }
  };

  const handleDelete = async (item: ISoftwareType) => {
    if (
      !window.confirm(
        `¿Eliminar el tipo "${item.displayName}"? Las instancias existentes no se borran.`
      )
    ) {
      return;
    }
    try {
      await deleteType.mutateAsync(item.id);
      toast.success("Tipo eliminado");
      if (editing?.id === item.id) resetEditor();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al eliminar");
    }
  };

  const isSaving = createType.isPending || updateType.isPending;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto gap-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <FolderCog className="h-4 w-4 text-muted-foreground" aria-hidden />
            Catálogo de software
          </DialogTitle>
          <DialogDescription className="text-xs">
            Tipos disponibles (PACS, RIS, laboratorio, etc.). Solo admin / Admin
            IT.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {mode === "idle" && (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  {types.length} tipo(s)
                </p>
                <Button
                  size="sm"
                  className="cursor-pointer"
                  onClick={startCreate}
                  disabled={isLoading}
                >
                  <Plus className="h-4 w-4 mr-1.5" aria-hidden />
                  Nuevo tipo
                </Button>
              </div>
              {isLoading ? (
                <div className="flex justify-center py-8" role="status">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : types.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  Catálogo vacío.
                </p>
              ) : (
                <ul className="max-h-[50vh] space-y-1.5 overflow-y-auto pr-0.5">
                  {types.map((item) => (
                    <li
                      key={item.id}
                      className={cn(
                        "flex items-center justify-between gap-2 rounded-lg border border-l-4 p-2.5 transition-colors hover:bg-muted/20",
                        item.active ? "border-l-emerald-500" : "border-l-border"
                      )}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="font-medium text-sm">{item.displayName}</p>
                          <Badge variant={item.active ? "default" : "secondary"}>
                            {item.active ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>
                        <p className="text-[11px] font-mono text-muted-foreground mt-0.5">
                          {item.key} · orden {item.order}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-9 cursor-pointer"
                          aria-label={`Editar ${item.displayName}`}
                          onClick={() => startEdit(item)}
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-9 cursor-pointer"
                          aria-label={`Eliminar ${item.displayName}`}
                          onClick={() => void handleDelete(item)}
                          disabled={deleteType.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {(mode === "create" || mode === "edit") && (
            <div className="space-y-3 rounded-lg border bg-muted/15 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">
                  {mode === "create"
                    ? "Nuevo tipo"
                    : `Editar: ${editing?.displayName}`}
                </Label>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-9 cursor-pointer"
                  aria-label="Cerrar formulario"
                  onClick={resetEditor}
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="st-name">Nombre</Label>
                  <Input
                    id="st-name"
                    value={displayName}
                    onChange={(e) => {
                      setDisplayName(e.target.value);
                      if (mode === "create") {
                        setKey(
                          e.target.value
                            .trim()
                            .toLowerCase()
                            .replace(/\s+/g, "_")
                        );
                      }
                    }}
                    placeholder="PACS"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="st-key">Key</Label>
                  <Input
                    id="st-key"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    placeholder="pacs"
                    className="font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="st-order">Orden</Label>
                  <Input
                    id="st-order"
                    type="number"
                    value={order}
                    onChange={(e) => setOrder(Number(e.target.value) || 0)}
                  />
                </div>
                <div className="flex items-center gap-2 sm:col-span-2">
                  <Switch
                    checked={active}
                    onCheckedChange={setActive}
                    id="st-active"
                  />
                  <Label htmlFor="st-active">Activo</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={resetEditor}
                >
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="cursor-pointer"
                  onClick={() => void handleSave()}
                  disabled={isSaving}
                >
                  {isSaving ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
